import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRecontactTemplate } from './recontact-template-context';
import { RejectionReason } from './rejection-reason.component';

const MAX_TEXT_LENGTH = 1024;

export const RecontactTemplateModal = () => {
  const { t } = useTranslation();
  const { state, isModalOpen, isSubmitting, closeModal, submit } =
    useRecontactTemplate();

  const rejected =
    state?.template?.status === 'rejected' ? state.template : null;
  const [text, setText] = useState('');

  useEffect(() => {
    if (isModalOpen) {
      setText(rejected?.text ?? state?.suggestedText ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  if (!state) {
    return null;
  }

  const isEmpty = !text.trim();

  return (
    <Modal isOpen={isModalOpen} onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent data-testid="recontact-template-modal">
        <ModalHeader>
          {t('manager.sections.templates.recontact_template.heading')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={4}>
            {t(
              rejected
                ? 'manager.sections.templates.recontact_template.resubmit'
                : 'manager.sections.templates.recontact_template.offer'
            )}
          </Text>
          {rejected?.reason && (
            <Text mb={4} color="red.600" fontSize="sm">
              {t(
                'manager.sections.templates.recontact_template.rejected_reason'
              )}{' '}
              <RejectionReason reason={rejected.reason} />
            </Text>
          )}
          <FormControl isInvalid={isEmpty}>
            <FormLabel>
              {t('manager.sections.templates.recontact_template.text_label')}
            </FormLabel>
            <Textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              maxLength={MAX_TEXT_LENGTH}
              rows={4}
              data-testid="recontact-template-text"
            />
            <FormErrorMessage>
              {t('manager.sections.templates.recontact_template.text_required')}
            </FormErrorMessage>
          </FormControl>
          <Text mt={3} fontSize="sm" color="gray.600">
            {t('manager.sections.templates.recontact_template.button_label', {
              text: state.buttonText,
            })}
          </Text>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="outline" onClick={closeModal}>
            {t(
              state.shouldOffer
                ? 'manager.sections.templates.recontact_template.controls.dismiss'
                : 'manager.sections.templates.recontact_template.controls.cancel'
            )}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit(text.trim())}
            isDisabled={isEmpty}
            isLoading={isSubmitting}
            data-testid="recontact-template-submit"
          >
            {t(
              rejected
                ? 'manager.sections.templates.recontact_template.controls.resubmit'
                : 'manager.sections.templates.recontact_template.controls.submit'
            )}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
